import { useCallback, useMemo } from 'react';
import { Box } from '@chakra-ui/react';
import { Text } from '@mercator-gcp/grout';
import { useNavigate } from 'react-router-dom';
import SelectionList from 'components/elements/SelectionList';
import { FlexRow } from 'components/layout/FlexRow';
import { useAvailableCities } from 'hooks/useUserCityAccess';
import { useDashboard } from 'state/dashboard';
import { METROPOLITAN_CODE_TO_LABEL } from 'utils/constants/location';
import { buildProjectMapURLWithParams } from 'utils/navigation';

const containerStyle = {
  height: '100%',
  marginTop: '240px',
};

const titleStyle = {
  marginBottom: 'padding_16',
  justifyContent: 'center',
};

const descriptionStyle = {
  marginBottom: 'padding_24',
  justifyContent: 'center',
};

const selectionStyle = {
  marginTop: 'padding_16',
  marginBottom: 'padding_16',
};

type TOptionType = {
  id: string;
  label: string;
  country: string;
};

const EmptyGroupPlaceholder = () => {
  const { availableRegions } = useAvailableCities();
  const { saveCities } = useDashboard();
  const navigate = useNavigate();

  const onSelectLocation = useCallback(
    (regionCode: string) => {
      navigate(
        buildProjectMapURLWithParams({
          regionCode,
        }),
      );
    },
    [saveCities],
  );

  const regionOptionsByCountry = useMemo(() => {
    return Object.keys(availableRegions).reduce<Record<string, TOptionType[]>>(
      (acc, key) => {
        const item = availableRegions[key];
        const countryKey = item.country.toUpperCase();
        if (!acc[countryKey]) {
          acc[countryKey] = [];
        }
        acc[countryKey].push({
          id: key,
          label: METROPOLITAN_CODE_TO_LABEL[key],
          country: item.country,
        });

        return acc;
      },
      {},
    );
  }, [availableRegions]);

  const sortedCountryNames = useMemo(() => {
    return Object.keys(regionOptionsByCountry).sort();
  }, [regionOptionsByCountry]);

  return (
    <Box sx={containerStyle}>
      <FlexRow sx={titleStyle}>
        <Text.H2>Get started with Mercator.ai</Text.H2>
      </FlexRow>
      <FlexRow sx={descriptionStyle}>
        <Text>
          Start searching early project opportunities in your markets. Searches you save
          will appear here.
        </Text>
      </FlexRow>
      {sortedCountryNames.map((countryName) => {
        return (
          <Box key={`city-options-for-${countryName}`}>
            <Text.SmallLabel>{countryName}</Text.SmallLabel>
            <SelectionList
              sx={selectionStyle}
              onSelect={onSelectLocation}
              options={regionOptionsByCountry[countryName]}
            />
          </Box>
        );
      })}
    </Box>
  );
};

export default EmptyGroupPlaceholder;
